import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Star, X, Save } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../data/constants";
import { INDUSTRIES } from "../../data/industries";

const EMPTY = {
  name: "",
  role: "",
  company: "",
  quote: "",
  rating: 5,
  photo_url: "",
  logo_url: "",
  region: "",
  industry: "",
  is_featured: true,
  sort_order: 0
};

const TestimonialForm = ({ initial, onSubmit, onCancel, busy }) => {
  const [form, setForm] = useState(initial || EMPTY);
  const update = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      rating: Number(form.rating),
      sort_order: Number(form.sort_order || 0),
      // Convert empty strings to null/empty so backend doesn't store ""
      role: form.role || null,
      company: form.company || null,
      photo_url: form.photo_url || null,
      logo_url: form.logo_url || null,
      region: form.region || null,
      industry: form.industry || null
    };
    await onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-card p-6 space-y-4" data-testid="testimonial-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-1">Name *</label>
          <input required value={form.name} onChange={update("name")} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none" data-testid="testimonial-input-name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-1">Role / Title</label>
          <input value={form.role || ""} onChange={update("role")} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-1">Company</label>
          <input value={form.company || ""} onChange={update("company")} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-1">Region</label>
          <select value={form.region || ""} onChange={update("region")} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none">
            <option value="">Both (US + UAE)</option>
            <option value="US">US only</option>
            <option value="UAE">UAE only</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-1">Industry (for industry page widget)</label>
          <select value={form.industry || ""} onChange={update("industry")} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none" data-testid="testimonial-input-industry">
            <option value="">— None (general) —</option>
            {INDUSTRIES.map((ind) => (
              <option key={ind.slug} value={ind.slug}>{ind.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-1">Photo URL</label>
          <input value={form.photo_url || ""} onChange={update("photo_url")} placeholder="https://..." className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-1">Company logo URL</label>
          <input value={form.logo_url || ""} onChange={update("logo_url")} placeholder="https://..." className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-1">Rating (1-5)</label>
          <input type="number" min={1} max={5} value={form.rating} onChange={update("rating")} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-1">Sort order</label>
          <input type="number" value={form.sort_order} onChange={update("sort_order")} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-dark mb-1">Quote *</label>
        <textarea required rows={4} value={form.quote} onChange={update("quote")} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none resize-none" data-testid="testimonial-input-quote" />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-dark">
        <input type="checkbox" checked={form.is_featured} onChange={update("is_featured")} className="w-4 h-4 accent-orange-primary" />
        Featured (show on homepage)
      </label>

      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-gray-medium hover:bg-gray-light flex items-center gap-1">
          <X size={16} /> Cancel
        </button>
        <button type="submit" disabled={busy} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="testimonial-submit-btn">
          <Save size={16} /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
};

const AdminTestimonialsPage = () => {
  const { authedAxios } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | testimonial object
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await authedAxios({ url: `${API}/testimonials` });
      setTestimonials(data);
    } catch (err) {
      setError("Could not load testimonials.");
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (payload) => {
    setBusy(true);
    setError(null);
    try {
      await authedAxios({ method: "post", url: `${API}/admin/testimonials`, data: payload });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = (id) => async (payload) => {
    setBusy(true);
    setError(null);
    try {
      await authedAxios({ method: "put", url: `${API}/admin/testimonials/${id}`, data: payload });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await authedAxios({ method: "delete", url: `${API}/admin/testimonials/${id}` });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-dark">Testimonials</h1>
          <p className="text-gray-medium">Real client quotes used on the homepage and SmartReach pages.</p>
        </div>
        {editing === null && (
          <button className="btn-primary flex items-center gap-2" onClick={() => setEditing("new")} data-testid="testimonial-new-btn">
            <Plus size={16} /> New
          </button>
        )}
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {editing === "new" && (
        <TestimonialForm onSubmit={handleCreate} onCancel={() => setEditing(null)} busy={busy} />
      )}
      {editing && editing !== "new" && (
        <TestimonialForm initial={editing} onSubmit={handleUpdate(editing.id)} onCancel={() => setEditing(null)} busy={busy} />
      )}

      <div className="space-y-3 mt-6">
        {testimonials.length === 0 && editing === null && (
          <div className="bg-white rounded-2xl p-8 text-center text-gray-medium">
            No testimonials yet. Click <strong>New</strong> to add the first one.
          </div>
        )}
        {testimonials.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl shadow-card p-5 flex items-start gap-4" data-testid={`testimonial-row-${t.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-display font-bold text-gray-dark">{t.name}</p>
                {t.role && <span className="text-sm text-gray-medium">· {t.role}</span>}
                {t.company && <span className="text-sm text-orange-primary font-medium">· {t.company}</span>}
                {t.region && <span className="text-xs bg-gray-light rounded-full px-2 py-0.5">{t.region}</span>}
                {t.industry && <span className="text-xs bg-orange-primary/10 text-orange-primary rounded-full px-2 py-0.5 font-medium">{t.industry}</span>}
                {t.is_featured && <Star size={14} className="text-orange-primary fill-orange-primary" />}
              </div>
              <p className="text-gray-medium text-sm italic">"{t.quote}"</p>
              <p className="text-xs text-gray-medium/70 mt-2">Rating: {t.rating}/5 · Order: {t.sort_order}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(t)} className="p-2 rounded-lg hover:bg-gray-light text-gray-medium" data-testid={`testimonial-edit-${t.id}`}>
                <Pencil size={16} />
              </button>
              <button onClick={() => handleDelete(t.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`testimonial-delete-${t.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminTestimonialsPage;
